import { Link } from 'react-router-dom';
import { ArrowRight, Wheat, Award, Globe, Package, CheckCircle, Shield, Leaf, Beaker, Truck, Users, Factory, FileCheck } from 'lucide-react';
import { ProcessInfographic } from '../components/ProcessInfographic';
import { ProcessVideo } from '../components/ProcessVideo';

const Home = () => {
  const featuredProducts = [
    {
      name: '1121 XXXL Steam',
      description: 'Extra-long grain basmati with exceptional aroma',
      highlight: 'Premium'
    },
    {
      name: '1121 Golden Sella',
      description: 'Parboiled rice with golden hue and rich nutrients',
      highlight: 'Popular'
    },
    {
      name: '1509 Basmati',
      description: 'Superior quality with excellent elongation',
      highlight: 'Value'
    },
    {
      name: 'Sugandha Rice',
      description: 'Aromatic variety with distinct fragrance',
      highlight: 'Fragrant'
    }
  ];

  const trustIndicators = [
    { value: '40+', label: 'Years of Milling Experience', icon: Award },
    { value: '25+', label: 'Countries Served', icon: Globe },
    { value: '100%', label: 'Quality Focused', icon: Shield },
    { value: 'Private Label', label: 'Packaging Solutions', icon: Package },
    { value: 'EU', label: 'Compliance Specialists', icon: FileCheck },
    { value: 'Global', label: 'Export Network', icon: Truck }
  ];

  const whyChoose = [
    {
      icon: Shield,
      title: 'EU-Compliant Rice Specialists',
      description: 'Specialized sourcing and processing of rice suitable for demanding international markets with strict quality standards.'
    },
    {
      icon: Factory,
      title: '40 Years of Milling Expertise',
      description: 'Generations of knowledge and experience in rice milling, grading, aging and quality control.'
    },
    {
      icon: Package,
      title: 'Private Labelling Solutions',
      description: 'Custom branding, multilingual packaging and retail-ready solutions for distributors and importers.'
    },
    {
      icon: Truck,
      title: 'Reliable Supply Chain',
      description: 'Consistent production planning and inventory management to ensure uninterrupted supply.'
    },
    {
      icon: Beaker,
      title: 'Advanced Quality Assurance',
      description: 'Multiple quality checkpoints from procurement to shipment.'
    },
    {
      icon: Globe,
      title: 'Global Export Experience',
      description: 'Understanding of international documentation, compliance and customer requirements.'
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1648102796336-0a5f2c417770?q=80&w=2000')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6 animate-fade-up">
              <div className="gold-line bg-gold" />
              <span className="font-body text-sm tracking-widest uppercase text-gold">
                Premium Indian Basmati Rice Exporter
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-up delay-100"
            >
              Premium Indian<br />
              <span className="text-gold">Basmati Rice</span><br />
              for Global Markets
            </h1>

            <p className="font-body text-base md:text-lg text-gold/90 tracking-wide mb-6 animate-fade-up delay-200">
              40 Years of Milling Excellence &middot; EU-Compliant Rice Specialists &middot; Private Labelling Experts
            </p>

            <p
              data-testid="hero-subtitle"
              className="font-body text-base md:text-lg text-white/80 max-w-2xl mb-10 leading-relaxed animate-fade-up delay-200"
            >
              Sawariya Global Exports LLP delivers premium Indian Basmati Rice to customers across the globe with a commitment to quality, consistency, food safety, and long-term partnerships.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="group bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase font-semibold inline-flex items-center justify-center gap-3 hover:bg-gold-light transition-all duration-300 rounded-sm"
              >
                Explore Our Products
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact?subject=Private+Label+Quote"
                data-testid="hero-cta-private-label"
                className="border border-gold/70 text-white px-8 py-4 font-body text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3 hover:bg-gold/10 hover:border-gold transition-all duration-300 rounded-sm"
              >
                Request Private Label Quote
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gold rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Trust Indicators Strip */}
      <section data-testid="trust-indicators" className="bg-navy-dark py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, #F5B041 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {trustIndicators.map((stat, idx) => (
              <div key={idx} data-testid={`trust-${idx}`} className="text-center group">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 border border-gold/30 mb-4 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                  <stat.icon className="w-6 h-6 text-gold" />
                </div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-gold mb-1">
                  {stat.value}
                </div>
                <div className="font-body text-xs tracking-widest uppercase text-white/70 leading-tight">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section data-testid="about-teaser" className="py-24 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1530507629858-e4977d30e9e0?w=800"
                  alt="Rice harvesting in paddy fields"
                  className="w-full h-[500px] object-cover"
                />
                {/* Overlay accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-navy/30 to-transparent" />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-gold text-navy-dark p-6 shadow-xl">
                <div className="font-heading text-4xl font-bold">35+</div>
                <div className="font-body text-sm tracking-widest uppercase">Years of Excellence</div>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                Our Heritage
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
                Rooted in Tradition,<br />
                <span className="text-gold">Grown</span> for Excellence
              </h2>
              <div className="gold-line bg-gold mb-8" />
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                Sawariya Global Exports LLP stands as a beacon of quality in the premium basmati rice 
                industry. Based in Haryana, India — the heartland of basmati cultivation — we combine 
                generations of expertise with modern processing standards.
              </p>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-8">
                Our commitment to pesticide-free rice and EU compliance has made us a trusted partner 
                for international buyers seeking the finest Indian basmati.
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Shield, text: 'EU Compliant' },
                  { icon: Leaf, text: 'Pesticide Free' },
                  { icon: Package, text: 'Private Labelling' },
                  { icon: Award, text: 'Certified Quality' }
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <feature.icon className="w-5 h-5 text-gold" />
                    <span className="font-body text-navy">{feature.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                data-testid="about-learn-more"
                className="group inline-flex items-center gap-2 font-body text-sm tracking-widest uppercase text-navy hover:text-gold transition-colors"
              >
                Learn Our Story
                <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section data-testid="featured-products" className="py-24 md:py-32 diagonal-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Our Collection
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Premium Rice Varieties
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our carefully curated selection of basmati rice, each variety 
              offering unique characteristics for discerning buyers
            </p>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {featuredProducts.map((product, index) => (
              <div
                key={index}
                data-testid={`featured-product-${index}`}
                className="group relative overflow-hidden bg-cream border border-transparent hover:border-gold transition-all duration-500"
              >
                {/* Highlight Badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span className="px-3 py-1 bg-gold/90 text-navy-dark text-xs tracking-widest uppercase font-body">
                    {product.highlight}
                  </span>
                </div>
                
                {/* Image */}
                <div className="h-48 overflow-hidden bg-cream-muted flex items-center justify-center">
                  <Wheat className="w-20 h-20 text-navy/20 group-hover:text-gold/40 transition-colors duration-500" />
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-navy mb-2 group-hover:text-gold transition-colors">
                    {product.name}
                  </h3>
                  <p className="font-body text-sm text-gray-600 mb-4">
                    {product.description}
                  </p>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 font-body text-xs tracking-widest uppercase text-navy group-hover:text-gold transition-colors"
                  >
                    View Details
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center">
            <Link
              to="/products"
              data-testid="view-all-products"
              className="inline-flex items-center gap-3 bg-navy text-white px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-navy-dark transition-colors"
            >
              View All Products
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Brands */}
      <section data-testid="our-brands" className="py-24 md:py-32 bg-gradient-to-b from-cream via-white to-cream relative overflow-hidden">
        {/* Background watermark */}
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "url('/sawariya-logo.png')",
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              House of Brands
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Our <span className="text-gold">Brands</span>
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From super-premium Basmati to everyday family favorites, our brands are crafted to serve diverse consumer needs across international markets while delivering the authentic taste of Indian rice.
            </p>
          </div>

          {/* Brand Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* SIFAH */}
            <Link
              to="/brands"
              data-testid="brand-card-sifah"
              className="brand-card group relative bg-gradient-to-br from-[#0F2D1F] via-[#1A4D2E] to-[#0A1F15] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, #F5B041 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }} />
              {/* Image */}
              <div className="relative h-72 flex items-center justify-center p-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/wmz0wks1_Sifah%20Basmati.jpeg"
                  alt="Sifah Premium Basmati Rice packaging"
                  className="h-full w-auto object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <span className="absolute top-4 right-4 px-3 py-1 bg-gold text-navy-dark text-[10px] tracking-[0.25em] uppercase font-bold rounded-full">
                  Flagship
                </span>
              </div>
              {/* Content */}
              <div className="p-7 text-white relative">
                <h3 className="font-heading text-3xl font-bold text-gold mb-1 tracking-wide">SIFAH</h3>
                <p className="font-accent italic text-sm text-white/85 mb-4">
                  Our Flagship Premium Basmati Experience
                </p>
                <div className="w-12 h-0.5 bg-gold mb-4" />
                <ul className="space-y-2 mb-6">
                  {[
                    'Premium 1121 Super Premium Basmati',
                    'Extra Long Grain & Naturally Aromatic',
                    'Export Quality Assured'
                  ].map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-gold font-body text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all">
                  Discover Sifah
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>

            {/* NOURA */}
            <Link
              to="/brands"
              data-testid="brand-card-noura"
              className="brand-card group relative bg-gradient-to-br from-[#5A0F1A] via-[#7A1A26] to-[#3D0810] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle at 75% 25%, #F4D58D 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }} />
              <div className="relative h-72 flex items-center justify-center p-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/bzfprd3v_Noura%20Basmati.png"
                  alt="Noura Biryani Basmati Rice packaging"
                  className="h-full w-auto object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <span className="absolute top-4 right-4 px-3 py-1 bg-[#F4D58D] text-[#5A0F1A] text-[10px] tracking-[0.25em] uppercase font-bold rounded-full">
                  Biryani Special
                </span>
              </div>
              <div className="p-7 text-white relative">
                <h3 className="font-heading text-3xl font-bold text-[#F4D58D] mb-1 tracking-wide">NOURA</h3>
                <p className="font-accent italic text-sm text-white/85 mb-4">
                  The Perfect Rice for Every Biryani
                </p>
                <div className="w-12 h-0.5 bg-[#F4D58D] mb-4" />
                <ul className="space-y-2 mb-6">
                  {[
                    'Premium Sella Biryani Rice',
                    'Excellent Grain Separation',
                    'Rich Taste & Restaurant-Quality Texture'
                  ].map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle className="w-4 h-4 text-[#F4D58D] flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-[#F4D58D] font-body text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all">
                  Discover Noura
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>

            {/* APNA DANA */}
            <Link
              to="/brands"
              data-testid="brand-card-apna-dana"
              className="brand-card group relative bg-gradient-to-br from-[#B8451C] via-[#D85C2A] to-[#8A2F13] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle at 50% 50%, #FFE9B0 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }} />
              <div className="relative h-72 flex items-center justify-center p-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/15sb2ndu_Apna%20Dana.PNG"
                  alt="Apna Dana Daily Basmati Rice packaging"
                  className="h-full w-auto object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <span className="absolute top-4 right-4 px-3 py-1 bg-[#FFE9B0] text-[#8A2F13] text-[10px] tracking-[0.25em] uppercase font-bold rounded-full">
                  Family Favorite
                </span>
              </div>
              <div className="p-7 text-white relative">
                <h3 className="font-heading text-3xl font-bold text-[#FFE9B0] mb-1 tracking-wide">APNA DANA</h3>
                <p className="font-accent italic text-sm text-white/85 mb-4">
                  Royal Taste for Every Family
                </p>
                <div className="w-12 h-0.5 bg-[#FFE9B0] mb-4" />
                <ul className="space-y-2 mb-6">
                  {[
                    'Economical Daily Use Rice',
                    'Great Value, Consistent Quality',
                    'Affordable Without Compromise'
                  ].map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                      <CheckCircle className="w-4 h-4 text-[#FFE9B0] flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-[#FFE9B0] font-body text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all">
                  Discover Apna Dana
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          </div>

          {/* Bottom statement + CTA */}
          <div className="text-center max-w-4xl mx-auto">
            <p className="font-body text-base md:text-lg text-gray-700 italic mb-8">
              &ldquo;Whether you&rsquo;re seeking world-class premium Basmati, specialized biryani rice, or affordable daily-use rice &mdash; Sawariya Global Exports offers a brand tailored to every market and every consumer.&rdquo;
            </p>
            <Link
              to="/brands"
              data-testid="explore-brands-cta"
              className="cta-quote-scrolled inline-flex items-center gap-3 text-white px-9 py-4 rounded-full font-body text-sm tracking-[0.18em] uppercase font-semibold"
            >
              <span className="relative z-10">Explore Our Brands</span>
              <ArrowRight size={18} className="cta-arrow relative z-10" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Process Infographic */}
      <ProcessInfographic />

      {/* Process Video Section */}
      <ProcessVideo />

      {/* Why Global Buyers Choose Sawariya */}
      <section data-testid="why-choose-us" className="py-24 md:py-32 dot-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Trusted by Importers Worldwide
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Why Global Buyers Choose <span className="text-gold">Sawariya</span>
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-gray-600 leading-relaxed">
              Four decades of milling expertise, EU-compliance specialization, and deep export experience make us the partner of choice for distributors, importers and retailers across continents.
            </p>
          </div>

          {/* 6 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, idx) => (
              <div
                key={idx}
                data-testid={`why-card-${idx}`}
                className="group relative bg-white border border-gray-100 p-8 rounded-xl shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                {/* Top gold accent line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold via-gold-light to-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                {/* Number watermark */}
                <span className="absolute top-4 right-4 font-heading text-7xl font-bold text-navy/[0.04] group-hover:text-gold/10 transition-colors">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="relative">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-navy text-gold mb-5 group-hover:bg-gold group-hover:text-navy-dark transition-colors duration-300">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                    {item.title}
                  </h3>
                  <p className="font-body text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Label / OEM Section */}
      <section data-testid="private-label" className="py-24 md:py-32 bg-navy-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-gold/5 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left content */}
            <div>
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                OEM &amp; White Label
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6 leading-tight">
                Build Your Own Rice<br />
                Brand <span className="text-gold">With Us</span>
              </h2>
              <div className="gold-line bg-gold mb-8" />
              <p className="font-body text-lg text-white/75 mb-10 leading-relaxed">
                We help importers, distributors and retailers launch successful rice brands through complete private labelling solutions — from custom packaging design to retail-ready shipments.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {[
                  'Custom Packaging Design',
                  'Plastic Pouches',
                  'Non-Woven Bags',
                  'Jute Bags',
                  'Multilingual Packaging',
                  'Retail & Wholesale Formats',
                  'OEM Manufacturing',
                  'Multiple SKU Sizes'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/85">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="font-body text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact?subject=Private+Label+Consultation"
                data-testid="private-label-cta"
                className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 rounded-full font-body text-sm tracking-[0.18em] uppercase font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all"
              >
                Request Private Label Consultation
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Right visual stack — pure info cards, no stock photos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                {/* 500+ SKUs */}
                <div className="bg-gradient-to-br from-gold to-gold-dark rounded-lg p-7 text-navy-dark relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/15" />
                  <Package className="w-9 h-9 mb-4 relative" />
                  <div className="font-heading text-5xl font-bold mb-1 relative">500<span className="text-3xl">+</span></div>
                  <div className="font-body text-xs tracking-[0.25em] uppercase font-semibold relative">
                    Custom SKUs Launched
                  </div>
                </div>
                {/* Pack formats */}
                <div className="bg-white/5 backdrop-blur-sm border border-gold/30 rounded-lg p-7 text-white">
                  <Wheat className="w-8 h-8 text-gold mb-3" />
                  <div className="font-heading text-2xl font-bold text-gold mb-4">5 Formats</div>
                  <ul className="font-body text-xs space-y-2 text-white/80">
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-gold rounded-full" />1 kg &middot; 5 kg &middot; 10 kg pouches</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-gold rounded-full" />20 kg jute bags</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-gold rounded-full" />50 kg bulk PP woven</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-gold rounded-full" />Custom retail cartons</li>
                  </ul>
                </div>
              </div>
              <div className="space-y-4 pt-12">
                {/* End-to-End */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-7 text-white">
                  <Award className="w-9 h-9 text-gold mb-4" />
                  <div className="font-heading text-xl font-bold mb-1">End-to-End</div>
                  <div className="font-body text-xs tracking-[0.25em] uppercase text-white/70 mb-4">
                    Brand Launch Support
                  </div>
                  <p className="font-body text-sm text-white/65 leading-relaxed">
                    From artwork &amp; barcoding to FSSAI label compliance &mdash; we handle the full stack.
                  </p>
                </div>
                {/* Languages supported */}
                <div className="bg-gradient-to-br from-navy via-navy-light to-navy rounded-lg p-7 text-white ring-1 ring-gold/30 relative overflow-hidden">
                  <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-gold/10 blur-2xl" />
                  <Globe className="w-9 h-9 text-gold mb-4 relative" />
                  <div className="font-heading text-4xl font-bold text-gold mb-1 relative">12+</div>
                  <div className="font-body text-xs tracking-[0.25em] uppercase text-white/75 mb-2 relative">
                    Languages on Pack
                  </div>
                  <p className="font-body text-[11px] text-white/60 leading-relaxed relative">
                    EN, AR, FR, ES, DE, NL, SW, HI &amp; more.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Without Compromise */}
      <section data-testid="quality-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Quality Assurance
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Quality Without <span className="text-gold">Compromise</span>
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-gray-600">
              Multiple checkpoints, certified labs, and hygienic processing — every grain is held to international export standards.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Beaker, title: 'Laboratory Testing', desc: 'NABL-accredited lab analyses for moisture, broken %, foreign matter & pesticide residue.' },
              { icon: FileCheck, title: 'Traceability', desc: 'Farm-to-bag tracking — every shipment can be traced to its source paddy lot.' },
              { icon: Factory, title: 'Hygienic Processing', desc: 'Modern mill with stainless equipment, dust extraction & food-grade conveyors.' },
              { icon: Shield, title: 'Food Safety Standards', desc: 'HACCP & ISO 22000 compliant operations with regular third-party audits.' },
              { icon: Award, title: 'Quality Certifications', desc: 'APEDA, FSSAI, USFDA, ISO 22000 & HACCP certified for global export.' },
              { icon: CheckCircle, title: 'Export Quality Control', desc: 'Pre-shipment inspections, container fumigation & documentation per destination norms.' }
            ].map((q, i) => (
              <div
                key={i}
                data-testid={`quality-${i}`}
                className="group bg-navy-dark/[0.02] border border-navy/10 rounded-xl p-6 hover:bg-navy hover:border-navy hover:-translate-y-1 transition-all duration-500"
              >
                <q.icon className="w-9 h-9 text-gold mb-4" />
                <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-white mb-2 transition-colors">
                  {q.title}
                </h3>
                <p className="font-body text-sm text-gray-600 group-hover:text-white/75 leading-relaxed transition-colors">
                  {q.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Export Markets */}
      <section data-testid="export-markets" className="py-24 md:py-32 bg-gradient-to-br from-navy-dark via-navy to-navy-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #F5B041 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
        {/* World silhouette decorative */}
        <div
          className="absolute inset-0 opacity-[0.08] bg-no-repeat bg-center bg-contain pointer-events-none"
          style={{ backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/World_map_-_low_resolution.svg/1200px-World_map_-_low_resolution.svg.png')" }}
        />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Global Reach
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Serving Customers Across <span className="text-gold">Continents</span>
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-white/75">
              From the Middle East to North America &mdash; our rice reaches consumers in 25+ countries through trusted distributor partnerships.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { region: 'Middle East', countries: 'UAE, Saudi Arabia, Kuwait, Qatar, Oman' },
              { region: 'Europe', countries: 'UK, Germany, Netherlands, France, Spain' },
              { region: 'Africa', countries: 'Kenya, Nigeria, South Africa, Tanzania' },
              { region: 'North America', countries: 'USA, Canada, Mexico' },
              { region: 'Asia-Pacific', countries: 'Australia, NZ, Singapore, Malaysia' }
            ].map((m, i) => (
              <div
                key={i}
                data-testid={`market-${i}`}
                className="bg-white/5 backdrop-blur-sm border border-gold/20 rounded-xl p-6 hover:bg-white/10 hover:border-gold/50 hover:-translate-y-1 transition-all duration-300"
              >
                <Globe className="w-7 h-7 text-gold mb-3" />
                <h3 className="font-heading text-lg font-bold text-white mb-2">{m.region}</h3>
                <p className="font-body text-xs text-white/65 leading-relaxed">{m.countries}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section data-testid="testimonials" className="py-24 md:py-32 bg-cream relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              What Our Partners Say
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Trusted by <span className="text-gold">Importers</span> Globally
            </h2>
            <div className="gold-line bg-gold mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: 'Sawariya has been our most reliable Basmati supplier for over six years. Consistent grain quality, on-time shipments, and exceptional private-label support.',
                name: 'Khalid R.',
                title: 'Importer, Dubai UAE',
                rating: 5
              },
              {
                quote: 'Their EU-compliance expertise gave us a competitive edge. Every container clears port inspection — pesticide reports are immaculate.',
                name: 'James M.',
                title: 'Distributor, London UK',
                rating: 5
              },
              {
                quote: 'From packaging design to retail-ready cartons, the Sawariya team handled our brand launch end-to-end. True OEM partner.',
                name: 'Aisha O.',
                title: 'Retail Brand Owner, Nairobi Kenya',
                rating: 5
              }
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 relative"
              >
                <div className="absolute -top-4 left-8 font-heading text-7xl text-gold leading-none">&ldquo;</div>
                <div className="flex items-center gap-1 mb-4 mt-2">
                  {[...Array(t.rating)].map((_, s) => (
                    <span key={s} className="text-gold text-lg">&#9733;</span>
                  ))}
                </div>
                <p className="font-body text-gray-700 leading-relaxed mb-6 relative">
                  {t.quote}
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <div className="font-heading font-semibold text-navy">{t.name}</div>
                  <div className="font-body text-xs tracking-widest uppercase text-gray-500 mt-1">{t.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-navy-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
            Ready to Source<br />
            <span className="text-gold">Premium Basmati Rice?</span>
          </h2>
          <p className="font-body text-lg text-white/70 max-w-2xl mx-auto mb-10">
            Partner with Sawariya Global Exports for reliable supply, competitive pricing,
            and uncompromising quality in every shipment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-get-quote"
              className="bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase font-semibold inline-flex items-center justify-center gap-3 hover:bg-gold-light transition-all duration-300 rounded-sm"
            >
              Request a Quote
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/products"
              className="border border-gold/50 text-white px-8 py-4 font-body text-sm tracking-widest uppercase inline-flex items-center justify-center hover:bg-gold/10 hover:border-gold transition-all duration-300 rounded-sm"
            >
              View Catalog
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
